/* eslint-disable @next/next/no-img-element */
'use client'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ViewProjectsButton } from '@/components/frontend/ViewProjectsButton'

import { About } from '@/payload-types'

const TRACK_H = 320
/** Space above the wave row so peak labels (above dots) stay inside this section without overlapping intro. */
const TIMELINE_TOP_RESERVE_PX = 200
const GHOST_W_FRAC = 0.165
const WAVE_PAGE_DESKTOP = 4
const WAVE_PAGE_MOBILE = 2
const MD_MEDIA = '(min-width: 768px)'
/** Dots sit nearer top/bottom so the spline clears copy between nodes. */
const WAVE_PEAK_Y = 46
const WAVE_TROUGH_Y = TRACK_H - 46
/** Gap from dot centre to nearest label edge (px). */
const LABEL_GAP_PEAK = 30
const LABEL_GAP_TROUGH = 34

function useIsMdBreakpoint() {
  const [isMd, setIsMd] = useState(true)
  useEffect(() => {
    const mq = window.matchMedia(MD_MEDIA)
    const apply = () => setIsMd(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])
  return isMd
}

function computePageStops(total: number, pageSize: number): number[] {
  if (total <= pageSize) return [0]
  const maxStart = total - pageSize
  const set = new Set<number>([0, maxStart])
  for (let s = pageSize; s < maxStart; s += pageSize) set.add(s)
  return [...set].sort((a, b) => a - b)
}

function pageIndexForGlobalMilestone(
  globalIdx: number,
  stops: number[],
  total: number,
  pageSize: number,
): number {
  for (let pi = 0; pi < stops.length; pi++) {
    const s = stops[pi]
    const end = Math.min(total, s + pageSize)
    if (globalIdx >= s && globalIdx < end) return pi
  }
  return Math.max(0, stops.length - 1)
}

function splitLines(text: string) {
  return text?.split('\n')?.filter(Boolean)
}

function timelineYearImage(year: string) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 520 200"
      fill="#111111"
      className="h-full w-full"
    >
      <text
        x="260"
        y="158"
        textAnchor="middle"
        fontFamily="Impact, 'Arial Narrow', sans-serif"
        fontSize="132"
        fontWeight="700"
        letterSpacing="-0.02em"
        fill="#111111"
        fillOpacity="0.12"
      >
        {year}
      </text>
    </svg>
  )
}

const TIMELINE_NODE_MARKER_SRC = '/about/timelineg.svg'
/** Marker size in wave viewBox units (matches ~old r=8 footprint at scale). */
const TIMELINE_NODE_MARK_VIEWBOX = 28

function buildCatmullPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return d
}

function TimelineNavButton({
  direction,
  onClick,
  disabled,
}: {
  direction: 'prev' | 'next'
  onClick?: () => void
  disabled?: boolean
}) {
  const isPrev = direction === 'prev'
  const label = isPrev ? 'Previous timeline' : 'Next timeline'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-[#111] bg-white text-[#111] shadow-sm transition hover:scale-[1.07] hover:bg-[#111] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#111] disabled:pointer-events-none disabled:opacity-35 disabled:hover:scale-100 disabled:hover:bg-white disabled:hover:text-[#111]"
    >
      {isPrev ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M15 4l-8 8 8 8V4z" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M9 4v16l8-8-8-8z" />
        </svg>
      )}
    </button>
  )
}

interface TimelineSectionProps {
  locale: AppLocale
  buttonLink?: string
  milestones: About['milestones']
}

const TIMELINE_TEXT = {
  en: {
    eyebrow: 'GLOBAL OPERATIONS ACROSS INDUSTRIES',
    heading: 'WE DELIVER FILTRATION WITH PRECISION AND EFFICIENCY',
    description:
      'Gallium Filters combines engineering expertise with advanced production capabilities to supply high-performance filtration solutions to industries worldwide. Our process ensures quality, consistency, and scalability at every stage.',
  },
  fr: {
    eyebrow: 'OPÉRATIONS MONDIALES DANS TOUS LES SECTEURS',
    heading: 'NOUS OFFRONS UNE FILTRATION PRÉCISE ET EFFICACE',
    description:
      "Gallium Filters associe une expertise d'ingénierie à des capacités de production avancées pour fournir des solutions de filtration haute performance aux industries du monde entier. Notre processus garantit la qualité, la constance et l'évolutivité à chaque étape.",
  },
  ar: {
    eyebrow: 'عمليات عالمية عبر مختلف الصناعات',
    heading: 'نقدم الترشيح بدقة وكفاءة',
    description:
      'تجمع جاليوم فلاترز بين الخبرة الهندسية وقدرات الإنتاج المتقدمة لتقديم حلول ترشيح عالية الأداء للصناعات حول العالم. تضمن عملياتنا الجودة والاتساق وقابلية التوسع في كل مرحلة.',
  },
}

export function TimelineSection({
  locale,
  buttonLink = '/insights',
  milestones,
}: TimelineSectionProps) {
  const t = TIMELINE_TEXT[locale]
  const isMd = useIsMdBreakpoint()
  const wavePageSize = isMd ? WAVE_PAGE_DESKTOP : WAVE_PAGE_MOBILE
  const isRTL = locale === 'ar'

  const pageStops = useMemo(
    () => computePageStops(milestones.length, wavePageSize),
    [milestones.length, wavePageSize],
  )
  const [stopIdx, setStopIdx] = useState(0)

  const viewStart = pageStops[Math.min(stopIdx, Math.max(0, pageStops.length - 1))] ?? 0

  const visibleMilestones = useMemo(() => {
    const items = milestones?.slice(viewStart, viewStart + wavePageSize)
    return isRTL ? [...items]?.reverse() : items
  }, [milestones, viewStart, wavePageSize, isRTL])

  const [pathD, setPathD] = useState('')
  const [dots, setDots] = useState<{ x: number; y: number }[]>([])
  const [viewW, setViewW] = useState(1000)
  const [layout, setLayout] = useState({ step: 120 })
  const waveContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setStopIdx(0)
  }, [locale, milestones.length, wavePageSize])

  useEffect(() => {
    setStopIdx((i) => Math.min(i, Math.max(0, pageStops.length - 1)))
  }, [pageStops])

  const updatePath = useCallback(() => {
    if (!isMd) {
      setPathD('')
      setDots([])
      setLayout({ step: 120 })
      return
    }
    const el = waveContainerRef.current
    if (!el || visibleMilestones.length < 2) {
      setPathD('')
      setDots([])
      setLayout({ step: 120 })
      return
    }
    const w = Math.max(el.offsetWidth, el.getBoundingClientRect().width, 320)
    const n = visibleMilestones.length
    const edgePad = Math.min(300, Math.max(96, Math.round((w * GHOST_W_FRAC) / 2 + 40)))
    const usable = Math.max(w - 2 * edgePad, 120)
    const step = usable / (n - 1)
    const points = visibleMilestones.map((_, i) => ({
      x: isRTL ? w - edgePad - i * step : edgePad + i * step,
      y: (viewStart + i) % 2 === 0 ? WAVE_TROUGH_Y : WAVE_PEAK_Y,
    }))
    const orderedPoints = isRTL ? [...points].reverse() : points
    setDots(orderedPoints)
    setPathD(buildCatmullPath(orderedPoints))
    setViewW(w)
    setLayout({ step })
  }, [visibleMilestones, viewStart, isMd, isRTL])

  useEffect(() => {
    updatePath()
    window.addEventListener('resize', updatePath)
    const el = waveContainerRef.current
    const ro =
      typeof ResizeObserver !== 'undefined' && el && isMd
        ? new ResizeObserver(() => {
            updatePath()
          })
        : null
    if (el && ro) ro.observe(el)
    return () => {
      window.removeEventListener('resize', updatePath)
      ro?.disconnect()
    }
  }, [updatePath, isMd])

  const goToPage = useCallback(
    (globalMilestoneIndex: number) => {
      const pi = pageIndexForGlobalMilestone(
        globalMilestoneIndex,
        pageStops,
        milestones.length,
        wavePageSize,
      )
      setStopIdx(pi)
    },
    [milestones.length, pageStops, wavePageSize],
  )

  const prevPage = () => {
    setStopIdx((i) => (isRTL ? Math.min(pageStops.length - 1, i + 1) : Math.max(0, i - 1)))
  }

  const nextPage = () => {
    setStopIdx((i) => (isRTL ? Math.max(0, i - 1) : Math.min(pageStops.length - 1, i + 1)))
  }

  return (
    <section
      className="relative block w-screen min-w-full bg-white py-12 md:py-16 min-[1920px]:py-20"
      style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-8 md:px-12 lg:px-16">
        <div className="max-w-[36rem]">
          <p className="font-poppins mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#111] md:text-xs">
            {t.eyebrow}
          </p>
          <h2 className="font-bebas-neue mb-3 text-2xl font-normal uppercase leading-[1.15] tracking-tight text-[#111] sm:text-3xl md:text-[clamp(1.75rem,4vw,2.35rem)]">
            {t.heading}
          </h2>
          <p className="font-poppins mb-6 text-sm font-normal leading-relaxed text-[#111] md:text-[15px] md:leading-[1.7]">
            {t.description}
          </p>
          <ViewProjectsButton href={buttonLink} className="self-start" />
        </div>
      </div>

      {milestones.length >= 2 ? (
        <>
          {/* Mobile: vertical spine + alternating cards (2 milestones per page) */}
          <div
            className="relative mx-auto mt-8 block max-w-lg px-4 pb-4 md:hidden"
            aria-label="Company milestones"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="relative min-h-[min(72vh,520px)] py-2">
              <div
                className="pointer-events-none absolute bottom-2 left-1/2 top-2 w-px -translate-x-1/2 bg-neutral-300"
                aria-hidden
              />
              <div className="relative z-[1] flex flex-col rtl:flex-col-reverse gap-2">
                {visibleMilestones.map((m, i) => {
                  const visualIndex = isRTL ? visibleMilestones.length - 1 - i : i
                  const globalIdx = viewStart + visualIndex
                  const onRight = globalIdx % 2 === 0
                  return (
                    <div
                      key={`mzig-${m.year}-${viewStart}`}
                      className={`relative flex w-full items-center gap-0 py-5 ${onRight ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      <div
                        className={`flex min-h-[100px] min-w-0 flex-1 flex-col ${onRight ? 'items-end pe-3 text-end' : 'items-start ps-3 text-start'}`}
                      >
                        <div
                          className={`pointer-events-none mb-1 block h-12 w-auto max-w-[min(11rem,48vw)] object-contain select-none ${onRight ? 'ms-auto' : ''}`}
                        >
                          {timelineYearImage(m.year)}
                        </div>
                        <div className="mt-2 w-full min-w-0 max-w-[min(100%,20rem)]">
                          <div className="bg-[#E40520] px-3 py-2.5 font-poppins text-[10px] font-bold uppercase leading-relaxed tracking-wide text-white break-words">
                            {splitLines(m.title)?.map((line, li) => (
                              <Fragment key={li}>
                                {li > 0 ? <br /> : null}
                                {line}
                              </Fragment>
                            ))}
                          </div>
                          <p className="font-poppins mt-3 text-[11px] leading-[1.7] tracking-[0.01em] text-neutral-600">
                            {splitLines(m.description)?.map((line, li) => (
                              <Fragment key={li}>
                                {li > 0 ? <br /> : null}
                                {line}
                              </Fragment>
                            ))}
                          </p>
                        </div>
                      </div>
                      <div className="relative z-[2] flex w-12 shrink-0 flex-col items-center justify-center self-stretch">
                        <button
                          type="button"
                          className="relative flex h-11 w-11 shrink-0 items-center justify-center p-0"
                          aria-label={`${m.year}`}
                          onClick={() => goToPage(globalIdx)}
                        >
                          <img
                            src={TIMELINE_NODE_MARKER_SRC}
                            alt={m.year}
                            width={73}
                            height={73}
                            className="h-10 w-10 object-contain"
                          />
                        </button>
                      </div>
                      <div className="min-w-0 flex-1" aria-hidden />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Desktop: horizontal wave */}
          <div
            ref={waveContainerRef}
            className="relative mt-8 hidden w-full overflow-visible px-0 pb-36 md:block"
            style={{ minHeight: TIMELINE_TOP_RESERVE_PX + TRACK_H }}
            aria-label="Company milestones"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {pathD && dots.length > 0 ? (
              <>
                <div
                  className="pointer-events-none"
                  aria-hidden
                  style={{ height: TIMELINE_TOP_RESERVE_PX }}
                />
                <svg
                  className="relative z-0 block w-full overflow-visible text-[#111]"
                  viewBox={`0 0 ${viewW} ${TRACK_H}`}
                  width="100%"
                  height={TRACK_H}
                  preserveAspectRatio="none"
                  role="presentation"
                >
                  {dots.map((pt, i) => {
                    const m = visibleMilestones[i]
                    if (!m) return null
                    const gw = viewW * GHOST_W_FRAC
                    const gh = TRACK_H * 0.4
                    const gx = pt.x - gw / 2
                    const gy =
                      (viewStart + i) % 2 === 0
                        ? isRTL
                          ? gh - TRACK_H / 4
                          : TRACK_H - gh - 12
                        : isRTL
                          ? TRACK_H - gh - 12
                          : gh - TRACK_H / 4
                    const scale = Math.min(gw / 520, gh / 200)
                    return (
                      <g
                        key={`ghost-${m.year}-${viewStart}`}
                        transform={`translate(${gx}, ${gy}) scale(${scale})`}
                        className="pointer-events-none select-none"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <text
                          x="260"
                          y="158"
                          textAnchor="middle"
                          fontFamily="Impact, 'Arial Narrow', sans-serif"
                          fontSize="132"
                          fontWeight="700"
                          letterSpacing="-0.02em"
                          fill="#111111"
                          fillOpacity="0.2"
                        >
                          {m.year}
                        </text>
                      </g>
                    )
                  })}
                  <path
                    d={pathD}
                    fill="none"
                    stroke="#E40520"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {dots.map((pt, i) => {
                    const visualIndex = isRTL ? visibleMilestones.length - 1 - i : i

                    const globalIdx = viewStart + visualIndex
                    return (
                      <g
                        key={`dot-${viewStart}-${i}`}
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => goToPage(globalIdx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            goToPage(globalIdx)
                          }
                        }}
                      >
                        <image
                          href={TIMELINE_NODE_MARKER_SRC}
                          x={pt.x - TIMELINE_NODE_MARK_VIEWBOX / 2}
                          y={pt.y - TIMELINE_NODE_MARK_VIEWBOX / 2}
                          width={TIMELINE_NODE_MARK_VIEWBOX}
                          height={TIMELINE_NODE_MARK_VIEWBOX}
                          preserveAspectRatio="xMidYMid meet"
                        />
                      </g>
                    )
                  })}
                </svg>

                {dots.map((pt, i) => {
                  const m = visibleMilestones[i]
                  if (!m) return null
                  const labelMaxW = Math.min(200, Math.max(112, Math.floor(layout.step * 0.88)))
                  const visualIndex = isRTL ? visibleMilestones.length - 1 - i : i

                  const globalIdx = viewStart + visualIndex
                  const isPeak = globalIdx % 2 === 1
                  return (
                    <div
                      key={`label-${m.year}-${viewStart}`}
                      role="group"
                      dir={locale === 'ar' ? 'rtl' : 'ltr'}
                      className={`pointer-events-none absolute z-[2] text-[#111] ${locale === 'ar' ? 'text-right' : 'text-left'}`}
                      style={{
                        left: pt.x,
                        top: TIMELINE_TOP_RESERVE_PX + pt.y,
                        width: labelMaxW,
                        transform: isPeak
                          ? `translate(-50%, calc(-100% - ${LABEL_GAP_PEAK}px))`
                          : `translate(-50%, ${LABEL_GAP_TROUGH}px)`,
                      }}
                    >
                      <p
                        className={`${locale === 'ar' ? 'font-poppins' : 'font-helvetica-neue'} text-[12px] font-semibold uppercase leading-[1.35] tracking-[0.07em] text-[#111] md:text-[13px] md:leading-[1.38] lg:text-[14px] lg:leading-[1.4]`}
                      >
                        {splitLines(m.title)?.map((line, li) => (
                          <Fragment key={li}>
                            {li > 0 ? <br /> : null}
                            {line}
                          </Fragment>
                        ))}
                      </p>
                      <p className="font-poppins mt-2 text-[11px] leading-[1.7] tracking-[0.015em] text-[#111] md:mt-2.5 md:text-[12px] md:leading-[1.75]">
                        {splitLines(m.description)?.map((line, li) => (
                          <Fragment key={li}>
                            {li > 0 ? <br /> : null}
                            {line}
                          </Fragment>
                        ))}
                      </p>
                    </div>
                  )
                })}
              </>
            ) : null}
          </div>
        </>
      ) : null}

      {milestones.length >= 2 ? (
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className="mt-8 flex w-full justify-center rtl:flex-row-reverse gap-4 px-4 pb-8 sm:px-8 md:mt-16 md:px-12 lg:mt-20 lg:px-16"
        >
          <TimelineNavButton
            direction="prev"
            onClick={prevPage}
            disabled={isRTL ? stopIdx >= pageStops.length - 1 : stopIdx <= 0}
          />
          <TimelineNavButton
            direction="next"
            onClick={nextPage}
            disabled={isRTL ? stopIdx <= 0 : stopIdx >= pageStops.length - 1}
          />
        </div>
      ) : null}
    </section>
  )
}
